import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { apiFetch } from "../lib/api";
import IdentifyPersonPopup, { UnknownPerson } from "../components/IdentifyPersonPopup";
import {useDemoMode} from "./DemoModeContext";

/* -------------------- Types -------------------- */
type Person = {
  id: number;
  name: string;
  room_number: string;
  role: string;
  photo_url: string | null;
};

type PersonIdentificationContextType = {
  unknownQueue: UnknownPerson[];
  totalQueueCount: number;
  showPopup: () => void;
  hidePopup: () => void;
};

/* -------------------- Context -------------------- */

const PersonIdentificationContext =
  createContext<PersonIdentificationContextType | null>(null);

export function usePersonIdentification() {
  const ctx = useContext(PersonIdentificationContext);
  if (!ctx) {
    throw new Error(
      "usePersonIdentification must be used within PersonIdentificationProvider"
    );
  }
  return ctx;
}

/* -------------------- Provider -------------------- */
type ProviderProps = {
  children: ReactNode;
};

export function PersonIdentificationProvider({ children }: ProviderProps) {
  const [unknownQueue, setUnknownQueue] = useState<UnknownPerson[]>([]);
  const [showIdentifyPopup, setShowIdentifyPopup] = useState(false);
  const { displayName } = useDemoMode();

  // People list for dropdowns
  const [people, setPeople] = useState<Person[]>([]);

  // ---------- Fetch people for dropdowns ----------
  const fetchPeople = useCallback(async () => {
    try {
      const res = await apiFetch("/get_residents", { cache: "no-store" });
      if (res.ok) {
        const data = await res.json();
        setPeople(data);
      }
    } catch (err) {
      console.error("Failed to fetch people:", err);
    }
  }, []);

  // ---------- Fetch pending items on mount (page refresh recovery) ----------
  const fetchPendingPersons = useCallback(async () => {
    try {
      const res = await apiFetch("/pending_visitors", { cache: "no-store" });
      if (res.ok) {
        const data = await res.json();
        if (data.pending && data.pending.length > 0) {
          setUnknownQueue(
            data.pending.map((p: any) => ({
              pending_id: p.pending_id,
              photo_url: p.photo_url,
              clip_url: p.clip_url || null,
              camera_id: p.camera_id,
              is_inside: p.is_inside,
              timestamp: p.timestamp,
              declined_resident_id: p.declined_resident_id,
              suggested_resident_id: p.suggested_resident_id,
              suggested_score: p.suggested_score,
              // NEW: carries through which pipeline produced this
              // pending entry — 'crossing' (entry/exit doorway) or
              // 'census' (surveillance/hallway camera). Defaults to
              // 'crossing' since that's the DB column's own default
              // for any row that predates this field.
              source_type: p.source_type || "crossing",
            }))
          );
        }
      }
    } catch (err) {
      console.error("Failed to fetch pending persons:", err);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchPeople();
    fetchPendingPersons();
  }, [fetchPeople, fetchPendingPersons]);

  // ---------- Auto-show popup when items arrive ----------
  useEffect(() => {
    if (unknownQueue.length > 0 && !showIdentifyPopup) {
      setShowIdentifyPopup(true);
      fetchPeople();
    }
  }, [unknownQueue, showIdentifyPopup, fetchPeople]);

  // ---------- Auto-close popup when queue becomes empty ----------
  useEffect(() => {
    if (unknownQueue.length === 0 && showIdentifyPopup) {
      setShowIdentifyPopup(false);
    }
  }, [unknownQueue.length, showIdentifyPopup]);

  // ---------- SSE Connection ----------
  useEffect(() => {
    const esRef = { current: null as EventSource | null };
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let closed = false;

    const handleMessage = async (raw: any) => {
      if (!raw) return;

      const msg =
        typeof raw === "string"
          ? (() => {
              try {
                return JSON.parse(raw);
              } catch {
                return null;
              }
            })()
          : raw;
      if (!msg) return;

      // Handle pending dismissal (another operator handled it)
      if (msg.type === "pending_dismissed") {
        console.log("[SSE] pending_dismissed:", msg.pending_id);
        setUnknownQueue((prev) =>
          prev.filter((v) => v.pending_id !== msg.pending_id)
        );
        return;
      }

      // Handle unknown-person events from BOTH pipelines:
      //   - unknown_person / unknown_visitor: entry/exit crossing pipeline
      //   - census_unknown_sighting: surveillance/hallway camera pipeline
      // Both land in the SAME queue and render through the SAME
      // IdentifyPersonPopup — the underlying confirm/register flow
      // (identify_as_resident, register_visitor, etc.) is identical
      // regardless of which camera produced the detection.
      if (
        msg.type === "unknown_person" ||
        msg.type === "unknown_visitor" ||
        msg.type === "census_unknown_sighting"
      ) {
        console.log("[SSE]", msg.type, ":", msg);

        setUnknownQueue((prev) => {
          if (prev.some((v) => v.pending_id === msg.pending_id)) {
            return prev;
          }
          return [
            ...prev,
            {
              pending_id: msg.pending_id,
              photo_url: msg.photo_url,
              clip_url: msg.clip_url || null,
              camera_id: msg.camera_id,
              is_inside: msg.is_inside,
              timestamp: msg.timestamp,
              declined_resident_id: msg.declined_resident_id,
              suggested_resident_id: msg.suggested_resident_id,
              suggested_score: msg.suggested_score,
              source_type:
                msg.type === "census_unknown_sighting" ? "census" : "crossing",
            },
          ];
        });
      }
    };

    const connect = () => {
      if (closed || esRef.current) return;

      const SSE_BASE = process.env.REACT_APP_SSE_URL!;
      const sseUrl = `${SSE_BASE}resident-updates?ts=${Date.now()}`;
      const es = new EventSource(sseUrl, { withCredentials: false });
      esRef.current = es;

      es.onopen = () => {
        console.log("[SSE PersonIdentification] open");
        if (retryTimer) {
          clearTimeout(retryTimer);
          retryTimer = null;
        }
      };

      es.onmessage = (evt) => {
        handleMessage(evt.data);
      };

      es.addEventListener("ping", () => {});

      es.onerror = () => {
        try {
          es.close();
        } catch {}
        esRef.current = null;
        if (!retryTimer && !closed) retryTimer = setTimeout(connect, 2000);
      };
    };

    const onUnload = () => {
      closed = true;
      if (retryTimer) clearTimeout(retryTimer);
      try {
        esRef.current?.close();
      } catch {}
      esRef.current = null;
    };

    window.addEventListener("beforeunload", onUnload);
    if (document.visibilityState === "visible") connect();
    const onVis = () => document.visibilityState === "visible" && connect();
    document.addEventListener("visibilitychange", onVis);

    return () => {
      window.removeEventListener("beforeunload", onUnload);
      document.removeEventListener("visibilitychange", onVis);
      onUnload();
    };
  }, []);

  // ---------- Identify Handlers ----------
  const handleIdentifyExisting = async (pendingId: string, personId: number) => {
    const person = people.find((p) => p.id === personId);
    const endpoint =
      person?.role === "visitor" ? "/api/identify/visitor" : "/api/identify/resident";
    const bodyKey = person?.role === "visitor" ? "visitor_id" : "resident_id";

    const res = await apiFetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        pending_id: pendingId,
        [bodyKey]: personId,
      }),
    });

    if (!res.ok) {
      throw new Error(`Failed to identify: ${res.status}`);
    }

    setUnknownQueue((prev) => prev.filter((v) => v.pending_id !== pendingId));
    await fetchPeople();
  };

  const handleRegisterNew = async (
    pendingId: string,
    data: { name: string; visiting_with_id: number | null; access_risk: boolean }
  ) => {
    const res = await apiFetch("/register_visitor", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        pending_id: pendingId,
        name: data.name,
        visiting_with_id: data.visiting_with_id,
        access_risk: data.access_risk,
      }),
    });

    if (!res.ok) {
      throw new Error(`Failed to register: ${res.status}`);
    }

    setUnknownQueue((prev) => prev.filter((v) => v.pending_id !== pendingId));
    await fetchPeople();
  };

  const handleRegisterStaff = async (
    pendingId: string,
    data: { name: string }
  ) => {
    const res = await apiFetch("/register_staff", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        pending_id: pendingId,
        name: data.name,
      }),
    });

    if (!res.ok) {
      throw new Error(`Failed to register staff: ${res.status}`);
    }

    setUnknownQueue((prev) => prev.filter((v) => v.pending_id !== pendingId));
    await fetchPeople();
  };

  const handleSkipUnknown = async (pendingId: string) => {
    const res = await apiFetch("/api/identify/skip", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ pending_id: pendingId }),
    });

    if (!res.ok) {
      throw new Error(`Failed to skip: ${res.status}`);
    }

    setUnknownQueue((prev) => prev.filter((v) => v.pending_id !== pendingId));
  };

  const handleSkipToBottomUnknown = (pendingId: string) => {
    setUnknownQueue((prev) => {
      const idx = prev.findIndex((v) => v.pending_id === pendingId);
      if (idx < 0) return prev;
      const next = [...prev];
      const [item] = next.splice(idx, 1);
      next.push(item);
      return next;
    });
  };

  // ---------- Close handler ----------
  const handleCloseIdentifyPopup = () => {
    setShowIdentifyPopup(false);
  };

  const showPopup = () => {
    if (unknownQueue.length > 0) {
      setShowIdentifyPopup(true);
    }
  };

  const hidePopup = () => {
    setShowIdentifyPopup(false);
  };

  // ---------- Render ----------
  const contextValue: PersonIdentificationContextType = {
    unknownQueue,
    totalQueueCount: unknownQueue.length,
    showPopup,
    hidePopup,
  };

  return (
    <PersonIdentificationContext.Provider value={contextValue}>
      {children}

      {showIdentifyPopup && unknownQueue.length > 0 && (
        <IdentifyPersonPopup
          queue={unknownQueue}
          people={people}
          onIdentifyExisting={handleIdentifyExisting}
          onRegisterNew={handleRegisterNew}
          onRegisterStaff={handleRegisterStaff}
          onSkip={handleSkipUnknown}
          onClose={handleCloseIdentifyPopup}
          onSkipToBottom={handleSkipToBottomUnknown}
        />
      )}
    </PersonIdentificationContext.Provider>
  );
}